/**
 * Ela Greens scroll reveal.
 *
 * Content rises and fades in once, when it first enters the viewport. Elements
 * opt in with [data-ela-reveal]; direct children of [data-ela-reveal-group]
 * stagger so a card grid resolves left to right rather than all at once.
 *
 * Three rules this follows:
 *  - Nothing is hidden unless JavaScript is running and motion is allowed, so
 *    the page is never blank for a crawler, a no-JS visitor, or a failed load.
 *  - The observer disconnects after revealing, so scrolling stays cheap.
 *  - prefers-reduced-motion disables the whole thing rather than shortening it.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) return;

  const REVEAL = '[data-ela-reveal], [data-ela-reveal-group] > *';

  const prime = (root) => {
    const targets = root.querySelectorAll(REVEAL);
    if (!targets.length) return;

    // Only now is it safe to hide: JS is running and motion is permitted.
    targets.forEach((el) => el.classList.add('ela-reveal'));

    const reveal = (el) => {
      const group = el.parentElement?.hasAttribute('data-ela-reveal-group');
      if (group) {
        const index = [...el.parentElement.children].indexOf(el);
        el.style.setProperty('--ela-reveal-delay', `${Math.min(index, 7) * 70}ms`);
      }
      el.classList.add('ela-reveal--in');
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          /* A keyboard End/PageDown or an anchor can jump completely past a
             target between observer samples. Passed content must never remain
             hidden just because its entrance animation was skipped. */
          if (!entry.isIntersecting && entry.boundingClientRect.bottom >= 0) return;
          reveal(el);
          observer.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    const pending = new Set();
    let sweepFrame = 0;

    const sweepPassedTargets = () => {
      sweepFrame = 0;
      pending.forEach((el) => {
        if (!el.isConnected) {
          pending.delete(el);
          return;
        }
        if (el.getBoundingClientRect().top >= window.innerHeight * 1.05) return;
        reveal(el);
        observer.unobserve(el);
        pending.delete(el);
      });
      if (!pending.size) window.removeEventListener('scroll', requestSweep, false);
    };

    function requestSweep() {
      if (sweepFrame) return;
      sweepFrame = requestAnimationFrame(sweepPassedTargets);
    }

    targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 1.05) {
        reveal(el);
      } else {
        observer.observe(el);
        pending.add(el);
      }
    });

    if (pending.size) window.addEventListener('scroll', requestSweep, { passive: true });
  };

  const enterHero = (root = document) => {
    const heroes = root.matches?.('[data-ela-hero-stage]')
      ? [root]
      : root.querySelectorAll('[data-ela-hero-stage]');

    heroes.forEach((hero) => {
      if (hero.dataset.elaHeroInitialised === 'true') return;
      hero.dataset.elaHeroInitialised = 'true';
      hero.classList.add('ela-hero--motion-ready');

      const portal = hero.querySelector('.ela-hero__portal-stage');
      if (portal && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        let tiltFrame = 0;
        let pointerX = 0;
        let pointerY = 0;

        const applyTilt = () => {
          tiltFrame = 0;
          hero.style.setProperty('--ela-portal-x', `${(pointerX * 7).toFixed(2)}px`);
          hero.style.setProperty('--ela-portal-y', `${(pointerY * 5).toFixed(2)}px`);
          hero.style.setProperty('--ela-portal-rx', `${(-pointerY * 3).toFixed(2)}deg`);
          hero.style.setProperty('--ela-portal-ry', `${(pointerX * 4).toFixed(2)}deg`);
        };

        hero.addEventListener('pointermove', (event) => {
          const rect = hero.getBoundingClientRect();
          pointerX = (event.clientX - rect.left) / rect.width - 0.5;
          pointerY = (event.clientY - rect.top) / rect.height - 0.5;
          if (!tiltFrame) tiltFrame = requestAnimationFrame(applyTilt);
        }, { passive: true });

        hero.addEventListener('pointerleave', () => {
          pointerX = 0;
          pointerY = 0;
          if (!tiltFrame) tiltFrame = requestAnimationFrame(applyTilt);
        });
      }

      requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('ela-hero--entered')));
    });
  };

  let parallaxItems = [];
  let parallaxFrame = 0;

  const updateParallax = () => {
    parallaxFrame = 0;
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    parallaxItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -80 || rect.top > viewport + 80) return;
      const depth = Number.parseFloat(item.dataset.elaParallax || '0.5');
      const centreOffset = (rect.top + rect.height / 2 - viewport / 2) / viewport;
      const shift = Math.max(-18, Math.min(18, centreOffset * -28 * depth));
      item.style.setProperty('--ela-depth-y', `${shift.toFixed(2)}px`);
    });
  };

  const requestParallax = () => {
    if (parallaxFrame) return;
    parallaxFrame = requestAnimationFrame(updateParallax);
  };

  const primeParallax = (root = document) => {
    const items = root.matches?.('[data-ela-parallax]')
      ? [root]
      : [...root.querySelectorAll('[data-ela-parallax]')];
    parallaxItems = [...new Set([...parallaxItems, ...items])].filter((item) => item.isConnected);
    requestParallax();
  };

  const start = () => {
    prime(document);
    enterHero(document);
    primeParallax(document);
    window.addEventListener('scroll', requestParallax, { passive: true });
    window.addEventListener('resize', requestParallax, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Horizon re-renders sections in the theme editor; re-prime what it swaps in.
  document.addEventListener('shopify:section:load', (event) => {
    prime(event.target);
    enterHero(event.target);
    primeParallax(event.target);
  });

  document.addEventListener('shopify:section:unload', () => {
    parallaxItems = parallaxItems.filter((item) => item.isConnected);
  });
})();
